import { API_ENDPOINTS } from "../config/api";

/**
 * A robust fetch wrapper that attempts to fetch from the local API first,
 * and falls back to the production API if the local request fails or returns a server error.
 * 
 * @param localUrl The local API URL (usually from API_ENDPOINTS)
 * @param options Standard fetch options
 * @returns Promise<Response>
 */
export async function robustFetch(localUrl: string, options?: RequestInit): Promise<Response> {
    try {
        const response = await fetch(localUrl, options);
        
        // If it's a 404, we don't fallback because the resource truly doesn't exist on local either.
        // But if it's a 5xx or the fetch itself fails, we fallback.
        if (response.ok || response.status === 404) {
            return response;
        }
        
        throw new Error(`Local API returned status ${response.status}`);
    } catch (error) {
        console.warn("Local API fetch failed, falling back to production:", error);
        
        const isDevelopment = process.env.NODE_ENV === 'development';
        if (!isDevelopment) {
            throw error;
        }

        try {
            const urlObj = new URL(localUrl);
            const prodUrl = `https://harivishnu-in.onrender.com${urlObj.pathname}${urlObj.search}`;
            
            // Retry loop for production fallback to handle Render cold-start delays
            let prodResponse: Response | null = null;
            let lastErr: any = null;

            for (let attempt = 0; attempt < 3; attempt++) {
                try {
                    prodResponse = await fetch(prodUrl, options);
                    if (prodResponse.ok || prodResponse.status === 404) {
                        return prodResponse;
                    }
                } catch (e) {
                    lastErr = e;
                    if (attempt < 2) {
                        await new Promise(res => setTimeout(res, 2000));
                    }
                }
            }

            if (prodResponse) return prodResponse;
            throw lastErr || new Error("Production API fallback failed");
        } catch (prodError) {
            console.error("Production API fallback also failed:", prodError);
            throw prodError;
        }
    }
}
